import React, { useEffect, useMemo, useState } from "react";
import Header from "./Header";
import SubHeader from "./SubHeader";
import DataTable, { formatNum } from "./DataTable";
import TableLoader from "../TableLoader";
import Apiauth from "../../../API/Apiauth";
import SKUCharts from "./graph/SKUCharts";
import TableTagFilterDropdown from "./TableTagFilterDropdown";
import "./report.scss";
import { toFormatPrice } from "../helpers/function";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGE_SIZE = 10;
const CHART_MAX_LIMIT = 100000;
const SUB_TABLE_DEFAULT_STATE = {
  sortCol: null,
  sortDir: "asc",
  searchTerms: {},
  draftFilters: {},
  appliedFilters: {},
};

function mapMetrics(summary = {}) {
  return [
    {
      label: "Total Sales Amount",
      value: toFormatPrice(Number(summary.total_sales_amount || 0)),
    },
    { label: "Total Qty Sold", value: formatNum(summary.total_qty_sold) },
    {
      label: "Top Selling SKU (by Amount)",
      value: summary.top_selling_sku_by_amount ?? "-",
    },
    {
      label: "Top Selling SKU (by Qty)",
      value: summary.top_selling_sku_by_qty ?? "-",
    },
  ];
}

function mapRows(data = []) {
  return data.map((row, idx) => ({
    _rowKey: row.mother_sku || row.sku || `sku-${idx}`,
    sku: row.sku || row.mother_sku || "-",
    main_images: row.main_images || "",
    qty: Number(row.qty) || 0,
    sales: Number(row.sales) || 0,
    inventory_price: Number(row.inventory_price) || 0,
    tag_price: Number(row.tag_price) || 0,
    qty_from_overall_sales: row.qty_from_overall_sales ?? 0,
    amount_from_overall_sales: row.amount_from_overall_sales ?? 0,
    children: Array.isArray(row.children)
      ? row.children.map((child, childIdx) => ({
          _id: `${row.sku || idx}-${child.sku || childIdx}`,
          variant: child.variant || child.sku || "-",
          product_id: child.product_id || child.sku || "-",
          metal: child.metal || "-",
          stone: child.stone || "-",
          size: child.size || "-",
          main_images: child.main_images || "",
          price: Number(child.inventory_price ?? child.tag_price) || 0,
          qty: Number(child.qty) || 0,
          amount: Number(child.sales) || 0,
        }))
      : [],
  }));
}

function normalizeFilters(filters = {}) {
  return {
    location: Array.isArray(filters.location) ? filters.location : [],
    group: Array.isArray(filters.group) ? filters.group : [],
    gender: Array.isArray(filters.gender) ? filters.gender : [],
    country: Array.isArray(filters.country) ? filters.country : [],
    sku: Array.isArray(filters.sku) ? filters.sku : [],
  };
}

const SKU = () => {
   const { t } = useTranslation()
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [totals, setTotals] = useState({});
  const [filters, setFilters] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedRows, setExpandedRows] = useState({});
  const [chartRows, setChartRows] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [showCharts, setShowCharts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subTableFilters, setSubTableFilters] = useState({});

  const fetchSalesBySKU = ({
    page: p = 1,
    limit: l = pageSize,
    ...extraFilters
  } = {}) => {
    setLoading(true);
    Apiauth(
      { page: p, limit: l, ...extraFilters },
      "Inventory/reports/salesBySKU",
    )
      .then((res) => {
        if (res?.code === 200) {
          setRows(mapRows(res.data));
          setMetrics(mapMetrics(res.summary));
          setTotals(res.totals ?? {});
          setFilters(normalizeFilters(res.filters ?? {}));
          setTotalRecords(res?.pagination?.total ?? 0);
          setPage(res?.pagination?.page ?? p);
          setPageSize(res?.pagination?.limit ?? l);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSalesBySKU({ page: 1, limit: DEFAULT_PAGE_SIZE });
  }, []);

  useEffect(() => {
    if (showCharts) {
      setLoading(true);
      Apiauth(
        { page: 1, limit: CHART_MAX_LIMIT, ...appliedFilters },
        "Inventory/reports/salesBySKU",
      )
        .then((res) => {
          if (res?.code === 200) {
            setChartRows(mapRows(res.data));
          }
        })
        .finally(() => setLoading(false));
    }
  }, [showCharts, appliedFilters]);

  const qtyOverallTotal = useMemo(
    () =>
      rows.reduce(
        (sum, row) => sum + (Number(row.qty_from_overall_sales) || 0),
        0,
      ),
    [rows],
  );
  const amountOverallTotal = useMemo(
    () =>
      rows.reduce(
        (sum, row) => sum + (Number(row.amount_from_overall_sales) || 0),
        0,
      ),
    [rows],
  );

  function toggleExpand(rowKey) {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }

  function handleFetchData({ page: newPage, limit: newLimit }) {
    if (newLimit !== pageSize) setPageSize(newLimit);
    setPage(newPage);
    fetchSalesBySKU({ ...appliedFilters, page: newPage, limit: newLimit });
  }

  function handleFilterApply(filterParams = {}) {
    setAppliedFilters(filterParams);
    setExpandedRows({});
    setSubTableFilters({});
    setPage(1);
    fetchSalesBySKU({ ...filterParams, page: 1, limit: pageSize });
  }

  function updateSubTableState(rowKey, updater) {
    setSubTableFilters((prev) => {
      const current = prev[rowKey] || SUB_TABLE_DEFAULT_STATE;
      const nextState =
        typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [rowKey]: nextState };
    });
  }

  const tableTotals = useMemo(
    () => ({
      qty: totals.qty,
      inventory_price: totals.inventory_price,
      tag_price: totals.tag_price,
      qty_from_overall_sales: qtyOverallTotal,
      amount_from_overall_sales: amountOverallTotal,
    }),
    [totals, qtyOverallTotal, amountOverallTotal],
  );

  const COLUMNS = useMemo(
    () => [
      { key: "_index", label: "#", align: "center", width: 40 },
      {
        key: "main_images",
        label: "Img",
        align: "center",
        width: 80,
        showFilter: false,
      },
      { key: "sku", label: "SKU", align: "left", width: 260 },
      { key: "qty", label: "Qty", align: "right", width: 120 },
      {
        key: "inventory_price",
        label: "Inventory Price",
        align: "right",
        width: 170,
      },
      { key: "tag_price", label: "Tag Price", align: "right", width: 170 },
      {
        key: "qty_from_overall_sales",
        label: "Qty % from over all sales",
        align: "right",
        width: 200,
      },
      {
        key: "amount_from_overall_sales",
        label: "Amount % from over all sales",
        align: "right",
        width: 220,
        isPriceColumn: true,
      },
    ],
    [],
  );

  function renderMetricValue(value, columnKey = "") {
    if (value == null || value === "") return "";
    if (typeof value === "string" && value.trim() === "") return "";
    if (typeof value === "string" && Number.isNaN(Number(value))) return value;
    const isMoneyColumn =
      /price/i.test(String(columnKey)) || columnKey === "amount_from_overall_sales";
    if (isMoneyColumn) return toFormatPrice(Number(value));
    return formatNum(value);
  }

  function renderCell(col, row) {
    const hasChildren = Array.isArray(row.children) && row.children.length > 0;
    const isExpanded = !!expandedRows[row._rowKey];

    if (col.key === "_index") {
      return row._rowIndex;
    }

    if (col.key === "main_images") {
      const imageSize = 22;
      if (!row.main_images) {
        return (
          <span
            style={{
              display: "inline-block",
              width: imageSize,
              height: imageSize,
              borderRadius: 2,
              background: "#f4f4f4",
            }}
          />
        );
      }
      return (
        <img
          src={row.main_images}
          alt={row.sku}
          style={{
            width: imageSize,
            height: imageSize,
            borderRadius: 2,
            objectFit: "cover",
            display: "inline-block",
          }}
        />
      );
    }

    if (col.key === "sku") {
      return (
        <div
          onClick={() => hasChildren && toggleExpand(row._rowKey)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            cursor: hasChildren ? "pointer" : "default",
          }}
        >
          <span style={{ color: "#5ea5c7" }}>{row.sku}</span>
          {hasChildren && (
            <span style={{ fontSize: 12, color: "#8b8b8b" }}>
              {isExpanded ? "▲" : "▼"}
            </span>
          )}
        </div>
      );
    }

    return renderMetricValue(row[col.key], col.key);
  }

  function renderSubImage(src, altText) {
    if (!src) {
      return (
        <span
          style={{
            display: "inline-block",
            width: 18,
            height: 18,
            borderRadius: 2,
            background: "#f4f4f4",
          }}
        />
      );
    }
    return (
      <img
        src={src}
        alt={altText}
        style={{
          width: 18,
          height: 18,
          borderRadius: 2,
          objectFit: "cover",
          display: "inline-block",
        }}
      />
    );
  }

  function renderExpandedRow(row) {
    if (!Array.isArray(row.children) || row.children.length === 0) return null;
    const subState = subTableFilters[row._rowKey] || SUB_TABLE_DEFAULT_STATE;
    const subHeaders = [
      { key: "img", label: "Img", align: "center", showFilter: false },
      { key: "variant", label: "Variant", align: "left", showFilter: true },
      {
        key: "product_id",
        label: "Product ID",
        align: "left",
        showFilter: true,
      },
      { key: "metal", label: "Metal", align: "left", showFilter: true },
      { key: "stone", label: "Stone", align: "left", showFilter: true },
      { key: "size", label: "Size", align: "left", showFilter: true },
      { key: "price", label: "Price", align: "right", showFilter: true },
      { key: "qty", label: "Qty", align: "right", showFilter: true },
      { key: "amount", label: "Amount", align: "right", showFilter: true },
    ];
    const numericColumns = new Set(["price", "qty", "amount"]);

    const getColumnKeyFromFilterKey = (filterKey) =>
      filterKey.includes("-sub-") ? filterKey.split("-sub-").pop() : filterKey;

    const getChildValueByKey = (child, key) => {
      if (key === "variant") return child.variant;
      if (key === "product_id") return child.product_id;
      if (key === "metal") return child.metal;
      if (key === "stone") return child.stone;
      if (key === "size") return child.size;
      if (key === "price") return child.price;
      if (key === "qty") return child.qty;
      if (key === "amount") return child.amount;
      return "";
    };

    const getAllColumnOptions = (key) => [
      ...new Set(
        row.children.map((child) => {
          const value = getChildValueByKey(child, key);
          return value == null ? "" : String(value);
        }),
      ),
    ];

    const getVisibleColumnOptions = (key) => {
      const allOptions = getAllColumnOptions(key);
      const term = (subState.searchTerms[key] || "").toLowerCase().trim();
      if (!term) return allOptions;
      return allOptions.filter((option) => option.toLowerCase().includes(term));
    };

    const getCurrentSelectedValues = (key) =>
      subState.draftFilters[key] ?? subState.appliedFilters[key] ?? [];

    const handleSubSort = ({ key, value }) => {
      const columnKey = getColumnKeyFromFilterKey(key);
      updateSubTableState(row._rowKey, (current) => ({
        ...current,
        sortCol: columnKey,
        sortDir: value === -1 ? "desc" : "asc",
      }));
    };

    const handleSubSearch = (event, filterKey) => {
      const columnKey = getColumnKeyFromFilterKey(filterKey);
      const value = event?.target?.value || "";
      updateSubTableState(row._rowKey, (current) => ({
        ...current,
        searchTerms: { ...current.searchTerms, [columnKey]: value },
      }));
    };

    const handleSubSelectAll = (event, filterKey) => {
      const columnKey = getColumnKeyFromFilterKey(filterKey);
      const visibleOptions = getVisibleColumnOptions(columnKey);
      const updated = event.target.checked ? visibleOptions : [];
      updateSubTableState(row._rowKey, (current) => ({
        ...current,
        draftFilters: { ...current.draftFilters, [columnKey]: updated },
      }));
    };

    const handleSubItemChange = (event, item, filterKey) => {
      const columnKey = getColumnKeyFromFilterKey(filterKey);
      const currentSelected = getCurrentSelectedValues(columnKey);
      const updated = event.target.checked
        ? [...new Set([...currentSelected, item])]
        : currentSelected.filter((value) => value !== item);
      updateSubTableState(row._rowKey, (current) => ({
        ...current,
        draftFilters: { ...current.draftFilters, [columnKey]: updated },
      }));
    };

    const handleSubClear = (filterKey) => {
      const columnKey = getColumnKeyFromFilterKey(filterKey);
      updateSubTableState(row._rowKey, (current) => ({
        ...current,
        searchTerms: { ...current.searchTerms, [columnKey]: "" },
        draftFilters: { ...current.draftFilters, [columnKey]: [] },
        appliedFilters: { ...current.appliedFilters, [columnKey]: [] },
      }));
    };

    const handleSubApply = (filterKey) => {
      const columnKey = getColumnKeyFromFilterKey(filterKey);
      const selected = getCurrentSelectedValues(columnKey);
      updateSubTableState(row._rowKey, (current) => ({
        ...current,
        appliedFilters: { ...current.appliedFilters, [columnKey]: selected },
      }));
    };

    const filteredChildren = row.children.filter((child) => {
      const activeKeys = Object.keys(subState.appliedFilters);
      if (!activeKeys.length) return true;
      return activeKeys.every((key) => {
        const selected = subState.appliedFilters[key] || [];
        if (!selected.length) return true;
        const value = getChildValueByKey(child, key);
        const normalized = value == null ? "" : String(value);
        return selected.includes(normalized);
      });
    });

    const displayChildren = subState.sortCol
      ? [...filteredChildren].sort((a, b) => {
          const va = getChildValueByKey(a, subState.sortCol);
          const vb = getChildValueByKey(b, subState.sortCol);
          const isNumeric = numericColumns.has(subState.sortCol);
          const cmp = isNumeric
            ? Number(va || 0) - Number(vb || 0)
            : String(va || "").localeCompare(String(vb || ""));
          return subState.sortDir === "asc" ? cmp : -cmp;
        })
      : filteredChildren;

    const headerClass = (align) =>
      align === "right"
        ? "dtbs-th-right"
        : align === "center"
          ? "dtbs-th-center"
          : "dtbs-th-left";

    const cellClass = (align) =>
      align === "right"
        ? "dtbs-td-right"
        : align === "center"
          ? "dtbs-td-center"
          : "dtbs-td-left";

    return (
      <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <table
          className="dtbs-table"
          style={{ borderTop: "none", minWidth: 1350 }}
        >
          <colgroup>
            <col style={{ width: 60 }} />
            <col style={{ width: 250 }} />
            <col style={{ width: 140 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 180 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 260 }} />
          </colgroup>
          <thead>
            <tr>
              {subHeaders.map((header) => (
                <th
                  key={header.key}
                  className={`${headerClass(header.align)} sku-thead`}
                >
                  {" "}
                  {header.label}
                  {header.showFilter && (
                    <TableTagFilterDropdown
                      filterKey={`${row._rowKey}-sub-${header.key}`}
                      renderInPortal
                      selectedValues={getCurrentSelectedValues(header.key)}
                      options={getVisibleColumnOptions(header.key)}
                      searchValue={subState.searchTerms[header.key] || ""}
                      onSortChange={handleSubSort}
                      onSearchKeyPress={handleSubSearch}
                      onSearchChange={handleSubSearch}
                      onSelectAllChange={handleSubSelectAll}
                      onItemChange={handleSubItemChange}
                      onClear={() =>
                        handleSubClear(`${row._rowKey}-sub-${header.key}`)
                      }
                      onApply={() =>
                        handleSubApply(`${row._rowKey}-sub-${header.key}`)
                      }
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayChildren.map((child, idx) => (
              <tr
                key={child._id}
                className={idx % 2 ? "dtbs-row-odd" : "dtbs-row-even"}
              >
                <td className={cellClass("center")}>
                  {renderSubImage(child.main_images, child.variant)}
                </td>
                <td className={cellClass("left")} style={{ color: "#5ea5c7" }}>
                  {t(child.variant)}
                </td>
                <td className={cellClass("left")}>{t(child.product_id)}</td>
                <td className={cellClass("left")}>{t(child.metal)}</td>
                <td className={cellClass("left")}>{t(child.stone)}</td>
                <td className={cellClass("left")}>{t(child.size)}</td>
                <td className={cellClass("right")}>
                  {toFormatPrice(Number(child.price))}
                </td>
                <td className={cellClass("right")}>{formatNum(child.qty)}</td>
                <td className={cellClass("right")}>
                  {toFormatPrice(Number(child.amount))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <>
      <Header title="Sales by SKU" metrics={metrics} />
      <SubHeader
        filters={filters}
        showCharts={showCharts}
        setShowCharts={setShowCharts}
        onFilterApply={handleFilterApply}
      />

      {loading ? (
        <TableLoader />
      ) : showCharts ? (
        <SKUCharts rows={chartRows} />
      ) : (
        <DataTable
          columns={COLUMNS}
          data={rows}
          totals={tableTotals}
          renderCell={renderCell}
          isRowExpanded={(row) =>
            !!expandedRows[row._rowKey] &&
            Array.isArray(row.children) &&
            row.children.length > 0
          }
          renderExpandedRow={renderExpandedRow}
          showSearch={true}
          showDateRange={true}
          pageLimit={pageSize}
          totalRecords={totalRecords}
          currentPage={page}
          onPageChange={setPage}
          fetchData={handleFetchData}
        />
      )}
    </>
  );
};

export default SKU;
